import React, { useEffect, useState } from 'react';
import { View, TouchableOpacity, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useAuth } from '../../../context/Auth';
import { translate } from '../../../services/translate';
import { dispatchService } from '../../../services/central-api/dispatch';
import FilterCheckboxList, { FilterListItem } from '../FilterCheckboxList';
import MyAppText from '../../MyAppText';

interface Props {
    value: number[];
    setValue: React.Dispatch<React.SetStateAction<number[]>>;
    actingBodyId: number;
    expandedCategory: number;
    setExpandedCategory: React.Dispatch<React.SetStateAction<number>>;
    onSelectSubCategory?: () => void;
}

export default function FinishReasonSubCategoryList({ value, setValue, actingBodyId, expandedCategory, setExpandedCategory, onSelectSubCategory }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [categories, setCategories] = useState<FilterListItem[]>([]);
    const [categoriesIsLoading, setCategoriesIsLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [subCategories, setSubCategories] = useState<FilterListItem[]>([]);
    const [subCategoriesIsLoading, setSubCategoriesIsLoading] = useState(false);

    async function loadCategories() {
        if (actingBodyId === 0) {
            setCategories([]);
            setCategoriesIsLoading(false);
            return;
        }
        try {
            setCategoriesIsLoading(true);
            const result = await dispatchService.getSimplifiedFinishReasons(actingBodyId);
            setCategories(result.map((category) => ({ label: category.name, value: category.id })));
        } catch (err) {
            console.error(err);
        } finally {
            setCategoriesIsLoading(false);
        }
    }

    async function loadSubCategories(finishReasonCategoryId: number) {
        try {
            setSubCategoriesIsLoading(true);
            const result = await dispatchService.getSimplifiedSubCategoriesFinishReason(finishReasonCategoryId);
            setSubCategories(result.map((sc) => ({ label: sc.name, value: sc.id })));
        } catch (err) {
            console.error(err);
        } finally {
            setSubCategoriesIsLoading(false);
        }
    }

    useEffect(() => {
        if (!authenticatedUser) {
            return;
        }
        loadCategories();
    }, [actingBodyId, authenticatedUser]);

    useEffect(() => {
        if (!expandedCategory) {
            setSubCategories([]);
            return;
        }
        setSubCategories([]);
        loadSubCategories(expandedCategory);
    }, [expandedCategory]);

    const toggleCategory = (categoryId: number) => {
        setExpandedCategory(prev => prev === categoryId ? 0 : categoryId);
    };

    const filtered = categories.filter(item =>
        item.label.toLowerCase().includes(search.toLowerCase())
    );

    const isEmpty = value.length === 0;

    return (
        <View style={styles.container}>
            {categories.length >= 7 && (
                <View style={styles.searchContainer}>
                    <FontAwesomeIcon icon={faMagnifyingGlass as IconDefinition} fontSize={16} color={theme.colors.neutral['300']} />
                    <TextInput
                        style={styles.searchInput}
                        placeholder={translate('typeToSearch')}
                        placeholderTextColor={theme.colors.neutral['300']}
                        value={search}
                        onChangeText={setSearch}
                    />
                </View>
            )}

            <View style={styles.countRow}>
                <MyAppText style={styles.countText}>
                    {translate('items')}: {filtered.length}
                </MyAppText>
                <TouchableOpacity onPress={() => setValue([])} disabled={isEmpty}>
                    <MyAppText style={[styles.clearSelection, isEmpty && styles.clearSelectionDisabled]}>
                        {translate('clearSelection')}
                    </MyAppText>
                </TouchableOpacity>
            </View>

            {categoriesIsLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size='small' color={theme.colors.primary['500']} />
                </View>
            ) : (
                <ScrollView style={styles.list} contentContainerStyle={{ gap: 8 }} nestedScrollEnabled>
                    {filtered.map((category) => {
                        const isExpanded = expandedCategory === category.value;
                        return (
                            <View key={category.value}>
                                <TouchableOpacity style={styles.categoryRow} onPress={() => toggleCategory(category.value)}>
                                    <View style={styles.categoryLeft}>
                                        <View style={[styles.radio, isExpanded && styles.radioSelected]}>
                                            {isExpanded && <View style={styles.radioInner} />}
                                        </View>
                                        <MyAppText style={styles.categoryLabel}>{category.label}</MyAppText>
                                    </View>
                                    <FontAwesomeIcon
                                        icon={(isExpanded ? faChevronUp : faChevronDown) as IconDefinition}
                                        fontSize={12}
                                        color={theme.colors.primary['500']}
                                    />
                                </TouchableOpacity>

                                {isExpanded && (
                                    <View style={styles.subContainer}>
                                        <FilterCheckboxList
                                            items={subCategories}
                                            value={value}
                                            setValue={(newValue) => {
                                                onSelectSubCategory?.();
                                                setValue(newValue);
                                            }}
                                            isLoading={subCategoriesIsLoading}
                                        />
                                    </View>
                                )}
                            </View>
                        );
                    })}
                </ScrollView>
            )}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        gap: 12,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 8,
    },
    searchInput: {
        flex: 1,
        fontSize: 16,
        color: theme.colors.neutral['400'],
        outlineStyle: 'none',
    },
    countRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    countText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.labelColor,
    },
    clearSelection: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    clearSelectionDisabled: {
        opacity: 0.4,
    },
    loadingContainer: {
        paddingVertical: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    list: {
        maxHeight: 320,
    },
    categoryRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    categoryLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    radio: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    radioSelected: {
        borderColor: theme.colors.primary['500'],
    },
    radioInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.colors.primary['500'],
    },
    categoryLabel: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    subContainer: {
        paddingLeft: 34,
        paddingTop: 12,
    },
}));
